// "Merge into another company..." - the super admin's way to fold a partner
// company record into another (owner, 2026-10-15: partners transfer "also in
// UI/workflow for super admin"; first case: a duplicate DP Electric partner
// record into the real DP Electric).
//
// Four steps, because the merge rewrites who a firm is on every project:
//   1. pick the company it goes into;
//   2. read the preview - what moves (per kind), the roster rows that become
//      one (with the flags that differ: the target's are kept), the people
//      who move, and every collision, with a choice where there is one;
//   3. type the merged company's name to confirm;
//   4. the result, with Undo.
// The server recomputes the plan on Merge and refuses if it changed since
// this preview, so what is shown here is what runs.
import React, { useCallback, useEffect, useState } from "react";
import { Modal, Select, Button, Alert, Table, Tag, Input, Checkbox, Spin } from "antd";
import { getMergeTargets, previewMerge, runMerge, undoMerge } from "api/partnerMerge/partnerMergeApi";

// The collections as people know them. Anything not here shows its own name.
const KIND_LABELS = {
  issues: "Issues",
  issuehours: "Logged hours",
  issueproposals: "Issue proposals",
  checklistitems: "Checklist items",
  ganttitems: "Plan activities",
  shopdrawings: "Shop drawings",
  projects: "Project shop-drawing defaults",
  contractorrateoverrides: "Contractor rates",
  itemrateoverrides: "Item rates",
  ignoredbillableitems: "Lines left out of the hours report",
  nocostmarks: "No-cost marks",
  projectshares: "Project shares",
  projectoversights: "Oversight entries",
  oversightlogs: "Oversight log entries",
  notificationrecipients: "Email recipient lists",
  notificationsettings: "Email switches",
  contractorlabelmaps: "Contractor name mappings",
  activitylogs: "Activity log entries",
  issueguests: "Guest links",
  roles: "Roles",
  fieldtasks: "Field tasks",
  elementstatusevents: "Tracking status events",
  minuteschangesets: "Meeting minutes changes",
  coordinationareas: "Coordination areas",
  licenses: "Pending invitations",
};
const label = (c) => KIND_LABELS[c.collection] || c.collection;
const show = (v) => (v === null || v === undefined || v === "" ? "-" : Array.isArray(v) ? v.join(", ") || "-" : typeof v === "boolean" ? (v ? "yes" : "no") : String(v));

export default function MergeCompanyDialog({ source, open, onClose, onDone }) {
  const [step, setStep] = useState("pick");
  const [targets, setTargets] = useState([]);
  const [targetId, setTargetId] = useState(undefined);
  const [plan, setPlan] = useState(null);
  const [keep, setKeep] = useState([]);
  const [typed, setTyped] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [result, setResult] = useState(null);
  const [drift, setDrift] = useState(null);
  const [undone, setUndone] = useState(null);

  useEffect(() => {
    if (!open) return;
    setStep("pick"); setTargetId(undefined); setPlan(null); setKeep([]); setTyped("");
    setError(""); setResult(null); setDrift(null); setUndone(null);
    getMergeTargets("").then((d) => setTargets(d.companies || [])).catch((e) => setError(e.message));
  }, [open]);

  const search = (q) => getMergeTargets(q).then((d) => setTargets(d.companies || [])).catch(() => {});

  const preview = useCallback(async (keepList) => {
    setBusy(true); setError("");
    try {
      const p = await previewMerge(source._id, targetId, keepList);
      setPlan(p); setStep("preview");
    } catch (e) {
      setError(e.message);
    } finally { setBusy(false); }
  }, [source, targetId]);

  const choose = (id, on) => {
    const next = on ? [...new Set([...keep, id])] : keep.filter((x) => x !== id);
    setKeep(next);
    preview(next);
  };

  const run = async () => {
    setBusy(true); setError("");
    try {
      const r = await runMerge(source._id, targetId, { keepTarget: keep, fingerprint: plan.fingerprint, confirmName: typed });
      setResult(r); setStep("done");
      if (onDone) onDone(r);
    } catch (e) {
      setError(e.message);
      if (e.plan) setPlan(e.plan);
    } finally { setBusy(false); }
  };

  const undo = async (force = false) => {
    setBusy(true); setError("");
    try {
      const r = await undoMerge(result.runId, force);
      setUndone(r); setDrift(null);
      if (onDone) onDone(r);
    } catch (e) {
      setError(e.message);
      if (e.drift) setDrift(e.drift);
    } finally { setBusy(false); }
  };

  const target = plan ? plan.target : targets.find((t) => t._id === targetId);
  const tName = target ? target.name : "the target";
  const choosable = plan ? plan.collisions.filter((c) => c.policy === "choose") : [];

  const footer = {
    pick: [
      <Button key="c" onClick={onClose}>Cancel</Button>,
      <Button key="p" type="primary" disabled={!targetId} loading={busy} onClick={() => preview(keep)}>Preview</Button>,
    ],
    preview: [
      <Button key="b" onClick={() => setStep("pick")}>Back</Button>,
      <Button key="c" onClick={onClose}>Cancel</Button>,
      <Button
        key="m" type="primary" danger loading={busy}
        disabled={!plan || plan.blocked || typed.trim() !== String(plan.source.companyName || "").trim()}
        onClick={run}
      >
        Merge
      </Button>,
    ],
    done: [<Button key="x" type="primary" onClick={onClose}>Close</Button>],
  }[step];

  return (
    <Modal
      title={`Merge ${source ? source.name : ""} into another company`}
      open={open}
      onCancel={onClose}
      footer={footer}
      width={860}
      destroyOnClose
    >
      {error && <Alert type="error" showIcon message={error} style={{ marginBottom: 12 }} />}

      {step === "pick" && (
        <div style={{ fontSize: 13, color: "#555", lineHeight: 1.6 }}>
          <p>
            Everything that belongs to <b>{source && source.name}</b> - its people, its rows on every project's
            roster, its issues, hours, checklist items, prices - will belong to the company you pick. Where both
            companies are on the same project, the two roster rows become one. <b>{source && source.name}</b> is
            kept as a locked record so the merge can be undone.
          </p>
          <div style={{ marginBottom: 6 }}>Merge into</div>
          <Select
            showSearch
            data-testid="merge-target"
            style={{ width: "100%" }}
            placeholder="Pick the company it really is"
            value={targetId}
            onChange={setTargetId}
            onSearch={search}
            filterOption={false}
            options={targets
              .filter((t) => source && t._id !== source._id)
              .map((t) => ({
                value: t._id,
                label: `${t.name}${t.kind === "partner" ? " (partner)" : ""}${t.suspended ? " (suspended)" : ""}`,
              }))}
          />
        </div>
      )}

      {step === "preview" && !plan && <Spin />}
      {step === "preview" && plan && (
        <div style={{ fontSize: 13 }}>
          {plan.blocked ? (
            <Alert
              type="warning" showIcon style={{ marginBottom: 12 }}
              message="This merge cannot run yet"
              description={<ul style={{ margin: 0, paddingLeft: 18 }}>{plan.hazards.filter((h) => h.blocking).map((h, i) => <li key={i}>{h.message}</li>)}</ul>}
            />
          ) : (
            <Alert type="info" showIcon style={{ marginBottom: 12 }}
              message={`${plan.totals.documents.toLocaleString()} documents change. A full backup is written first; the result has an Undo.`} />
          )}
          {plan.hazards.filter((h) => !h.blocking).map((h, i) => (
            <Alert key={i} type="info" message={h.message} style={{ marginBottom: 8 }} />
          ))}

          <h6 style={{ marginTop: 12 }}>What moves to {tName}</h6>
          {plan.counts.length ? (
            <Table
              size="small" pagination={false} rowKey={(c) => `${c.collection}.${c.path}`}
              dataSource={plan.counts}
              columns={[
                { title: "Kind", render: (_, c) => label(c) },
                { title: "Field", dataIndex: "path", render: (v) => <span style={{ color: "#888" }}>{v}</span> },
                { title: "Rows", dataIndex: "count", width: 90, render: (v) => (v === null ? <Tag color="red">could not count</Tag> : v) },
              ]}
            />
          ) : <div style={{ color: "#888" }}>No issues, hours, prices or other records name {plan.source.name}.</div>}

          <h6 style={{ marginTop: 16 }}>Project rosters</h6>
          {plan.participants.merged.map((m) => (
            <div key={m.sourceRowId} style={{ border: "1px solid #eee", borderRadius: 6, padding: 10, marginBottom: 8 }}>
              <b>{m.project}</b>: both companies are on this project, so the two rows become one.{" "}
              {m.addedUsers.length + m.addedSupervisors.length
                ? `Joining ${tName}'s row: ${[...m.addedUsers, ...m.addedSupervisors.map((e) => `${e} (supervisor)`)].join(", ")}.`
                : `Everyone is already on ${tName}'s row.`}
              {m.statusFrom !== m.statusTo && ` The row becomes ${m.statusTo} (it was ${m.statusFrom}).`}
              {m.differences.length > 0 && (
                <Table
                  size="small" pagination={false} style={{ marginTop: 6 }} rowKey="field"
                  dataSource={m.differences}
                  columns={[
                    { title: "Setting", dataIndex: "label" },
                    { title: `${tName} (kept)`, dataIndex: "target", render: show },
                    { title: `${plan.source.name} (dropped)`, dataIndex: "source", render: show },
                  ]}
                />
              )}
            </div>
          ))}
          {plan.participants.repointed.length > 0 && (
            <div>Re-pointed to {tName}: {plan.participants.repointed.map((r) => r.project).join(", ")}</div>
          )}
          {/* No "also joins the target's row on other projects" list since
              2026-10-15: the merge re-files people only on the projects they
              are already on (owner: "only show specific people on specific
              projects"). */}
          {!plan.participants.merged.length && !plan.participants.repointed.length && (
            <div style={{ color: "#888" }}>{plan.source.name} is on no project roster.</div>
          )}

          <h6 style={{ marginTop: 16 }}>People</h6>
          {plan.users.length ? (
            <ul style={{ paddingLeft: 18 }}>
              {plan.users.map((u) => (
                <li key={u._id}>
                  {u.name || u.email} <span style={{ color: "#888" }}>{u.email}</span>
                  {u.activeMoves ? ` - moves to ${tName}` : u.membershipChanges ? ` - their ${plan.source.name} membership becomes ${tName}` : ""}
                  {u.roleChanges ? ` - gets ${tName}'s role of the same name` : ""}
                  {u.notes.length > 0 && <span style={{ color: "#a15c00" }}> ({u.notes.join("; ")})</span>}
                </li>
              ))}
            </ul>
          ) : <div style={{ color: "#888" }}>Nobody's account names {plan.source.name}.</div>}

          {plan.collisions.length > 0 && (
            <>
              <h6 style={{ marginTop: 16 }}>Where both companies have their own</h6>
              {choosable.length > 1 && (
                <Button size="small" style={{ marginBottom: 8 }}
                  onClick={() => { const all = choosable.map((c) => c.id); setKeep(all); preview(all); }}>
                  Keep {tName}'s for all {choosable.length}
                </Button>
              )}
              <Table
                size="small" pagination={false} rowKey="id"
                dataSource={plan.collisions}
                columns={[
                  { title: plan.source.name, dataIndex: "source" },
                  { title: tName, dataIndex: "target" },
                  {
                    title: "",
                    width: 220,
                    render: (_, c) =>
                      c.policy === "choose" ? (
                        <Checkbox checked={keep.includes(c.id)} disabled={busy} onChange={(e) => choose(c.id, e.target.checked)}>
                          Keep {tName}'s
                        </Checkbox>
                      ) : c.policy === "keepTarget" ? (
                        <span style={{ color: "#888" }}>{tName}'s is kept; {plan.source.name}'s stays on the locked record</span>
                      ) : (
                        <span style={{ color: "#c0392b" }}>{c.remedy}</span>
                      ),
                  },
                ]}
              />
            </>
          )}

          {!plan.blocked && (
            <div style={{ marginTop: 18 }}>
              <div style={{ marginBottom: 6 }}>
                Type <b>{plan.source.companyName}</b> to confirm
              </div>
              <Input data-testid="merge-confirm" value={typed} onChange={(e) => setTyped(e.target.value)} placeholder={plan.source.companyName} />
            </div>
          )}
        </div>
      )}

      {step === "done" && result && (
        <div style={{ fontSize: 13, lineHeight: 1.7 }}>
          {!undone ? (
            <>
              <Alert type="success" showIcon message={`${result.source.name} is now part of ${result.target.name}`} style={{ marginBottom: 12 }} />
              <div>
                {result.result.documents} records re-pointed, {result.result.participantsMerged} roster row(s) merged,{" "}
                {result.result.participantsRepointed} re-pointed, {result.result.users} account(s) updated.
              </div>
              {result.skipped.length > 0 && (
                <div>Left on the locked {result.source.name} record (kept {result.target.name}'s): {result.skipped.map((s) => s.what).join("; ")}</div>
              )}
              <div style={{ color: "#888" }}>Backup: {result.backup.documents} documents saved before anything changed.</div>
              {drift && (
                <Alert
                  type="warning" showIcon style={{ marginTop: 12 }}
                  message="Changed since the merge - undoing puts these back to how they were before it"
                  description={<ul style={{ margin: 0, paddingLeft: 18 }}>{drift.map((d) => <li key={`${d.collection}:${d._id}`}>{d.label}</li>)}</ul>}
                />
              )}
              <div style={{ marginTop: 12 }}>
                {drift ? (
                  <Button danger loading={busy} onClick={() => undo(true)}>Undo anyway</Button>
                ) : (
                  <Button loading={busy} onClick={() => undo(false)}>Undo this merge</Button>
                )}
              </div>
            </>
          ) : (
            <Alert type="info" showIcon message={`Undone: ${undone.restored} document(s) put back${undone.removed ? `, ${undone.removed} removed` : ""}. ${undone.sourceName} is its own company again.`} />
          )}
        </div>
      )}
    </Modal>
  );
}
